import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { ArrowRightLeft, Clock, CheckCircle2, XCircle, FileText, ExternalLink, Loader2, Plus, X, Upload, Search } from 'lucide-react';
import VolverOnboardingBtn from '../../components/VolverOnboardingBtn';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// --- Helpers RUT ---
const formatRutOnChange = (val) => {
  const c = (val || '').replace(/[^0-9kK]/g, '').toUpperCase();
  if (!c) return '';
  if (c.length === 1) return c;
  const body = c.slice(0, -1);
  const dv = c.slice(-1);
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`;
};
const validarRut = (rut) => {
  const clean = (rut || '').replace(/[.\-\s]/g, '').toUpperCase();
  if (clean.length < 2) return false;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  if (!/^\d+$/.test(body)) return false;
  let sum = 0, mult = 2;
  for (let i = body.length - 1; i >= 0; i--) { sum += parseInt(body[i], 10) * mult; mult = mult === 7 ? 2 : mult + 1; }
  const r = sum % 11;
  const calc = r === 0 ? '0' : r === 1 ? 'K' : String(11 - r);
  return dv === calc;
};

const fmtDT = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return iso; }
};

const badge = (estado) => {
  if (estado === 'pendiente') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-700"><Clock className="w-3 h-3" />Pendiente</span>;
  if (estado === 'aprobada') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3" />Aprobada</span>;
  if (estado === 'rechazada') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-red-100 text-red-700"><XCircle className="w-3 h-3" />Rechazada</span>;
  return <span className="text-xs text-slate-500">{estado}</span>;
};

export default function EseReasignaciones() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [rutInput, setRutInput] = useState('');
  const [rutError, setRutError] = useState('');
  const [checking, setChecking] = useState(false);
  const [target, setTarget] = useState(null); // {trabajador_id, nombre, apellido, empresa_origen_nombre, ...}
  const [motivo, setMotivo] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const checkTimer = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ese/reasignaciones`, { headers });
      setRows(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar'); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const resetCreate = () => {
    setShowCreate(false); setRutInput(''); setRutError(''); setTarget(null);
    setMotivo(''); setFile(null);
  };

  const handleRutChange = (val) => {
    const f = formatRutOnChange(val);
    setRutInput(f);
    setTarget(null); setRutError('');
    if (checkTimer.current) clearTimeout(checkTimer.current);
    if (f.replace(/[.\-]/g, '').length < 2) return;
    if (!validarRut(f)) { setRutError('Dígito verificador inválido'); return; }
    checkTimer.current = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await axios.get(`${API}/ese/trabajadores/check-rut`, { headers, params: { rut: f } });
        const s = res.data;
        if (s.status === 'belongs_to_other') {
          setTarget(s);
          setRutError('');
        } else if (s.status === 'already_in_my_empresa') {
          setRutError('Este RUT ya está en su empresa. No requiere reasignación.');
        } else if (s.status === 'exists_no_empresa') {
          setRutError('Este RUT existe pero no tiene empresa titular. Vaya a "Trabajadores" y créelo directamente.');
        } else {
          setRutError('Este RUT no existe en la plataforma. Vaya a "Trabajadores" y créelo normalmente.');
        }
      } catch (e) { setRutError(e.response?.data?.detail || 'Error al validar el RUT'); }
      setChecking(false);
    }, 500);
  };

  const submit = async () => {
    if (!target) return;
    if (!file) { toast.error('Debe adjuntar la carta PDF'); return; }
    if (file.type !== 'application/pdf') { toast.error('La carta debe ser PDF'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('El archivo excede 5 MB'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const up = await axios.post(
        `${API}/storage/upload?module=reasignaciones&parent_id=${target.trabajador_id}`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      await axios.post(`${API}/ese/reasignaciones`, {
        trabajador_id: target.trabajador_id,
        motivo: motivo || null,
        carta_bucket: up.data.bucket,
        carta_path: up.data.path,
        carta_filename: file.name,
      }, { headers });
      toast.success('Solicitud de reasignación enviada al Capacitador');
      resetCreate(); fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar solicitud');
    }
    setSubmitting(false);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="ese-reasignaciones">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <VolverOnboardingBtn />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ArrowRightLeft className="w-6 h-6 text-teal-600" />Solicitudes de Reasignación</h1>
            <p className="text-xs text-slate-500 mt-0.5">Historial de trabajadores solicitados a otras empresas.</p>
          </div>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold text-sm shadow-sm"
          data-testid="btn-nueva-reasignacion">
          <Plus className="w-4 h-4" />Nueva Solicitud
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <ArrowRightLeft className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500 mb-4">No hay solicitudes de reasignación aún.</p>
          <button onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold text-sm">
            <Plus className="w-4 h-4" />Crear la primera solicitud
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-slate-500 font-medium">RUT</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Nombre</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Empresa Origen</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Solicitado</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Estado</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Carta</th>
                <th className="text-left px-4 py-3 text-slate-500 font-medium">Motivo Rechazo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`row-reasig-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.rut}</td>
                  <td className="px-4 py-3">{r.nombre_snapshot}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{r.empresa_origen_nombre || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDT(r.fecha_solicitud)}</td>
                  <td className="px-4 py-3">{badge(r.estado)}</td>
                  <td className="px-4 py-3">
                    {r.carta_url ? (
                      <a href={r.carta_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline">
                        <FileText className="w-3 h-3" /> Ver <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-red-600">{r.motivo_rechazo || <span className="text-slate-300">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Nueva Solicitud */}
      {showCreate && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => !submitting && resetCreate()}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()} data-testid="modal-nueva-reasig">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Nueva Solicitud de Reasignación</h3>
                <p className="text-xs text-slate-500 mt-1">Ingresa el RUT del trabajador que quieres reasignar a tu empresa.</p>
              </div>
              <button onClick={() => !submitting && resetCreate()} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">RUT del trabajador *</label>
                <div className="relative mt-1">
                  <input value={rutInput} onChange={e => handleRutChange(e.target.value)}
                    placeholder="12.345.678-9"
                    className={`w-full px-3 py-2 pr-9 text-sm border rounded-lg ${rutError ? 'border-red-300' : target ? 'border-emerald-300' : 'border-slate-200'}`}
                    data-testid="reasig-rut-input" />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checking ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> :
                     target ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
                     rutError ? <XCircle className="w-4 h-4 text-red-500" /> :
                     <Search className="w-4 h-4 text-slate-300" />}
                  </div>
                </div>
                {rutError && <p className="text-[11px] text-red-500 mt-1">{rutError}</p>}
              </div>

              {target && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                  <p className="text-xs text-emerald-900">
                    <span className="font-semibold">{(target.nombre || '') + ' ' + (target.apellido || '')}</span>
                    {' '}pertenece actualmente a{' '}
                    <span className="font-semibold">{target.empresa_origen_nombre}</span>.
                  </p>
                  <p className="text-[11px] text-emerald-800 mt-1">
                    Al aprobar la reasignación, el trabajador deberá rehacer la inducción bajo su empresa.
                  </p>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-slate-600">Carta de Reasignación (PDF) *</label>
                <input type="file" accept="application/pdf"
                  onChange={e => setFile(e.target.files?.[0] || null)}
                  className="mt-1 w-full text-xs" data-testid="reasig-file-input" />
                {file && <p className="text-[10px] text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600">Motivo (opcional)</label>
                <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
                  placeholder="Ej. Trabajador se cambió a nuestra empresa el 01-09-2026."
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button type="button" disabled={submitting} onClick={resetCreate}
                className="px-4 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                Cancelar
              </button>
              <button type="button" disabled={submitting || !target || !file}
                onClick={submit}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold disabled:opacity-50"
                data-testid="reasig-submit-btn">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Enviando...</> : <><Upload className="w-4 h-4" />Enviar solicitud</>}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
